import { useTheme } from "@mui/material";
import { getM3Colors } from "../../theme/style/m3Palette";
import { ORG_PALETTE } from "../../theme/style/organizationPalette";
import styles from "./m3.module.css";

// Inline CSS variables for a tone. Harmonized with the HTML report & M3 organization palette
const toneVariables = (tone, isDark) => {
  const toneKey =
    tone === "error" ? "scope1" :
    tone === "warning" ? "scope2" :
    tone === "info" ? "scope3" :
    tone === "primary" ? "gwp" :
    tone;

  const card = ORG_PALETTE.metricCards[toneKey];
  if (card) {
    if (isDark) {
      return {
        "--kpi-accent": card.iconColorDark,
        "--kpi-bg": card.bgDark,
        "--kpi-border": card.borderDark,
        "--kpi-icon-bg": card.iconBgDark,
        "--kpi-text": "var(--m3-on-surface)",
      };
    }
    // Light mode - exact clean, clear pastel styling matching the HTML report
    const borderTint = typeof card.bg === "string" && card.bg.includes("0.10")
      ? card.bg.replace("0.10", "0.20")
      : "var(--m3-outline-variant)";
    const iconBgTint = typeof card.bg === "string" && card.bg.includes("0.10")
      ? card.bg.replace("0.10", "0.18")
      : "rgba(0, 0, 0, 0.04)";

    return {
      "--kpi-accent": card.accent,
      "--kpi-bg": card.bg,
      "--kpi-border": borderTint,
      "--kpi-icon-bg": iconBgTint,
      "--kpi-text": "var(--m3-on-surface)",
    };
  }

  const m3 = getM3Colors(isDark);
  return {
    "--kpi-accent": m3.onSurfaceVariant,
    "--kpi-bg": isDark ? m3.surfaceContainerLow : m3.surfaceContainerLowest,
    "--kpi-border": m3.outlineVariant,
    "--kpi-icon-bg": isDark ? "rgba(255, 255, 255, 0.06)" : "rgba(0, 0, 0, 0.04)",
    "--kpi-text": "var(--m3-on-surface)",
  };
};

/**
 * Headline metric tile: label, value with an optional unit, optional footer.
 * The value is shown as given; format numbers and units before passing them in.
 *
 * @param {React.ReactNode} label
 * @param {React.ReactNode} value
 * @param {string} unit - small text after the value, e.g. "tCO₂e" or "%"
 * @param {React.ReactNode} icon
 * @param {React.ReactNode} footer
 * @param {"default"|"primary"|"chain"|"scope1"|"scope2"|"scope3"|"gwp"|"cost"|"shield"} tone
 * @param {"left"|"right"} iconPosition - "left" puts the icon in a box beside the text
 * @param {"md"|"lg"} size - "lg" is the dashboard headline size
 */
const KpiTile = ({ label, value, unit, icon, footer, tone = "default", iconPosition = "left", size = "md" }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const style = toneVariables(tone, isDark);
  const sizeClass = size === "lg" ? styles.kpiLg : "";

  if (iconPosition === "left") {
    return (
      <div className={`${styles.kpi} ${styles.kpiLeft} ${sizeClass}`} style={style}>
        {icon && <div className={styles.kpiIconBox}>{icon}</div>}
        <div className={styles.kpiContent}>
          <div className={styles.kpiLabel}>
            <span className={styles.kpiLabelText} title={typeof label === "string" ? label : undefined}>
              {label}
            </span>
          </div>
          <div className={styles.kpiValue}>
            {value}
            {unit && <span className={styles.kpiUnit}>{unit}</span>}
          </div>
          {footer && <div className={styles.kpiFoot}>{footer}</div>}
        </div>
      </div>
    );
  }

  return (
    <div className={`${styles.kpi} ${sizeClass}`} style={style}>
      <span className={styles.kpiLabel}>
        <span className={styles.kpiLabelText}>{label}</span>
        {icon && <span className={styles.kpiIcon}>{icon}</span>}
      </span>
      <span className={styles.kpiValue}>
        {value}
        {unit && <span className={styles.kpiUnit}>{unit}</span>}
      </span>
      {footer && <span className={styles.kpiFoot}>{footer}</span>}
    </div>
  );
};

export default KpiTile;
